import type { LiteralUnion } from 'type-fest';

/**
 * Supported log levels.
 */
export const supportedLogLevels = [
  'trace',
  'debug',
  'info',
  'warn',
  'error',
  'silent',
] as const;

/**
 * Known logger namespaces.
 */
export const knownLoggerNamespaces = ['core', 'auth', 'i18n', 'api'] as const;

/**
 * Log level
 */
export type LogLevel = (typeof supportedLogLevels)[number];

/**
 * Logger namespace
 */
export type LoggerNamespace = LiteralUnion<
  | (typeof knownLoggerNamespaces)[number]
  | `${(typeof knownLoggerNamespaces)[number]}.${string}`,
  string
>;

/**
 * An interface where all logger must implement.
 */
export interface ILogger {
  /**
   * Log a message with the trace level.
   * @param namespace - The namespace to log the message from.
   * @param rest - The rest of the arguments to log.
   */
  trace(namespace: LoggerNamespace, ...rest: any[]): void;

  /**
   * Log a message with the debug level.
   * @param namespace - The namespace to log the message from.
   * @param rest - The rest of the arguments to log.
   */
  debug(namespace: LoggerNamespace, ...rest: any[]): void;

  /**
   * Log a message with the info level.
   * @param namespace - The namespace to log the message from.
   * @param rest - The rest of the arguments to log.
   */
  info(namespace: LoggerNamespace, ...rest: any[]): void;

  /**
   * Log a message with the warn level.
   * @param namespace - The namespace to log the message from.
   * @param rest - The rest of the arguments to log.
   */
  warn(namespace: LoggerNamespace, ...rest: any[]): void;

  /**
   * Log a message with the error level.
   * @param namespace - The namespace to log the message from.
   * @param error - The error to log.
   * @param data - The additional data to log.
   * @param data.message - The message to log.
   * @param data.tags - The tags to log.
   * @param data.extras - The extras to log.
   * @param sendErrorLog - Whether to send the error log to Sentry.
   */
  error(
    namespace: LoggerNamespace,
    error: Error,
    data?: {
      message?: string;
      tags?: Record<string, string>;
      extras?: Record<string, unknown>;
    },
    sendErrorLog?: boolean
  ): void;
}
